import _styled from '@emotion/styled/base';
import React__default from 'react';
import { Flex } from '../Flex/index.js';
import { SkeletonLoader } from '../SkeletonLoader/index.js';
import { testIds } from './testIds.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';
import { Spacing } from '../primitives/spacing.js';
import { density, text, size } from '@hubble/tokens/web';

const INTERACTIVE_CELL_DATA_ATTR = 'data-hbl-table-interactive-cell';
const INTERACTIVE_CELL_TEXT_UNDERLINE_DATA_ATTR = 'data-interactive-cell-text-underline';
const SIZE_MAP = {
  md: '52px',
  lg: '84px',
  auto: 'auto'
};
const ALIGN_MAP = {
  center: 'center',
  end: 'flex-end',
  start: 'flex-start'
};
const DENSITY_TYPESET_MAP = {
  sm: 'xs',
  md: 'sm',
  lg: 'md'
};
const Table = _styled('table', {
  target: "e1rrl8nv9"
})({
  name: "cnn2pp",
  styles: "display:block;width:100%;height:100%"
} );
const TBody = _styled('tbody', {
  target: "e1rrl8nv8"
})("display:block;height:100%;", ({
  overflowY
}) => Boolean(overflowY) && `overflow-y: ${overflowY};`, ";");
const TRow = _styled('tr', {
  target: "e1rrl8nv7"
})("display:flex;background:", ({
  theme
}) => theme.colorScheme.list.background.enabled, ";", ({
  density
}) => Boolean(density) && 'position: relative;', " z-index:0;", ({
  density: density$1
}) => Boolean(density$1) && `gap: ${density.contentItems.gap[density$1]};`, ";border-bottom:", ({
  isExpanded,
  theme
}) => isExpanded ? 'none' : `1px solid ${theme.colorScheme.list.border.enabled}`, ";transition:border-bottom 0s ", ({
  isExpanded,
  expandAnimationDuration = 0.2
}) => isExpanded ? '0s' : `${expandAnimationDuration}s`, ";&:hover:not([aria-hidden='true']),&:focus-within{", ({
  density: density$1,
  theme,
  windowed
}) => density$1 && !windowed ? `
          &::before {
            content: "";
            position: absolute;
            inset: 0px -${density.contentItems.padding.left[density$1]} 0px -${density.contentItems.padding.right[density$1]};
            background: ${theme.colorScheme.list.background.hover};
            z-index: -1;
          }` : `background: ${theme.colorScheme.list.background.hover};
    `, ";}&:last-of-type{border-bottom:unset;}");
const THeaderRow = _styled('tr', {
  target: "e1rrl8nv6"
})("display:flex;background:", ({
  theme
}) => theme.colorScheme.list.background.enabled, ";", ({
  density: density$1
}) => Boolean(density$1) && `gap: ${density.contentItems.gap[density$1]};`, ";border-bottom:1px solid ", ({
  theme
}) => theme.colorScheme.list.border.enabled, ";");
const THead = _styled('thead', {
  target: "e1rrl8nv5"
})(({
  offset
}) => ({
  display: 'block',
  margin: 0,
  ...(typeof offset !== 'undefined' ? {
    position: 'sticky',
    top: offset,
    zIndex: 1
  } : {})
}));
const TCellBase = _styled('td', {
  target: "e1rrl8nv4"
})("display:flex;flex:1;align-items:center;justify-content:", ({
  justifyContent
}) => ALIGN_MAP[justifyContent] || ALIGN_MAP.start, ";margin:0;", ({
  density
}) => !density && `padding: 0 ${Spacing.scale[1]};`, " min-width:0;overflow:hidden;color:", ({
  theme
}) => theme.colorScheme.content.primary, ";");
const TCell = _styled(TCellBase, {
  target: "e1rrl8nv3"
})(({
  density: density$1,
  size
}) => density$1 ? `
    height: ${density.contentItems.height[density$1]};
    font-size: ${text.typesets.body[DENSITY_TYPESET_MAP[density$1]].fontSize};
    font-weight: ${text.typesets.body[DENSITY_TYPESET_MAP[density$1]].fontWeight};
    line-height: ${text.typesets.body[DENSITY_TYPESET_MAP[density$1]].lineHeight};
    ` : `
    height: ${SIZE_MAP[size]};
    font-size: ${size === 'md' ? text.typesets.body.sm.fontSize : text.typesets.body.md.fontSize};
    font-weight: ${text.typesets.body.sm.fontWeight};
    line-height: ${size === 'md' ? text.typesets.body.sm.lineHeight : text.typesets.body.md.lineHeight};
  `, " ", ({
  resizable
}) => resizable && `
    flex: none;
  `, " justify-content:", ({
  justifyContent
}) => ALIGN_MAP[justifyContent] || ALIGN_MAP.start, ";text-align:", ({
  justifyContent
}) => justifyContent || 'start', ";white-space:nowrap;overflow:hidden;text-overflow:ellipsis;>*{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;}>[", INTERACTIVE_CELL_DATA_ATTR, "]{display:flex;height:100%;width:calc(100% + ", Spacing.scale[2], ");margin-inline-end:-", Spacing.scale[1], ";margin-inline-start:-", Spacing.scale[1], ";padding-inline-start:", Spacing.scale[1], ";padding-inline-end:", Spacing.scale[1], ";align-items:center;", ({
  justifyContent
}) => `
      ${justifyContent ? `
        justify-content: ${ALIGN_MAP[justifyContent]};
        text-align: ${justifyContent};
      ` : `
        justify-content: ${ALIGN_MAP.start};
        text-align: start;
      `}
      padding: 0 ${Spacing.scale[justifyContent === 'end' ? 0.5 : 1]};
    `, " outline:none;}&:first-of-type{", ({
  density
}) => !density ? `padding-inline-start: ${Spacing.scale[2.5]};` : 'padding-left: 0;', ">[", INTERACTIVE_CELL_DATA_ATTR, "]{width:calc(100% + ", Spacing.scale[1], ");margin-inline-end:-", Spacing.scale[1], ";margin-inline-start:0;padding-inline-start:0;padding-inline-end:", Spacing.scale[1], ";}}&:last-of-type{", ({
  density,
  resizable
}) => !density ? `padding-inline-end: ${Spacing.scale[2.5]};` : `padding-right: ${resizable ? Spacing.scale[0.5] : '0'};`, ">[", INTERACTIVE_CELL_DATA_ATTR, "]{width:calc(100% + ", Spacing.scale[1], ");margin-inline-end:0;margin-inline-start:-", Spacing.scale[1], ";padding-inline-start:", Spacing.scale[1], ";padding-inline-end:0;}}", ({
  density,
  justifyContent,
  resizable
}) => density && justifyContent === 'end' && resizable && `
    padding-right: 4px;
  `, ";");
const Resizer = _styled('div', {
  target: "e1rrl8nv2"
})("position:absolute;top:0;right:0;height:", ({
  density: density$1,
  rowCount
}) => `calc(${rowCount} * ${density.contentItems.height[density$1] || '84px'} + ${density$1 === 'sm' ? '28px' : '40px'} + ${rowCount - 1}px);`, " width:4px;background:", ({
  theme
}) => theme.colorScheme.background.secondary, ";cursor:ew-resize;user-select:none;touch-action:none;opacity:0;", ({
  isResizing
}) => isResizing && `background: #023790;`, ";");
const LinkCell = ({
  as = 'a',
  route,
  ...rest
}) => {
  const baseProps = {
    ...rest,
    [INTERACTIVE_CELL_DATA_ATTR]: true,
    css: {
      outline: 'none',
      '&:hover,&:focus': {
        '[data-interactive-cell-text-underline]': {
          textDecoration: 'underline',
          textUnderlineOffset: 2
        }
      }
    }
  };
  const Component = as === 'a' ? 'a' : as;
  return Component === 'a' ? jsx(Component, {
    ...baseProps,
    href: route
  }) : jsx(Component, {
    ...baseProps,
    to: route
  });
};
var _ref = {
  name: "1w4r364",
  styles: "-webkit-appearance:none;border-radius:0;text-align:inherit;background:none;box-shadow:none;padding:0;cursor:pointer;border:none;color:inherit;font:inherit;outline:none;&:hover,&:focus{[data-interactive-cell-text-underline]{text-decoration:underline;text-underline-offset:2px;}}"
} ;
function ButtonCell(props) {
  const btnProps = {
    ...props,
    [INTERACTIVE_CELL_DATA_ATTR]: true
  };
  return jsx("button", {
    ...btnProps,
    css: _ref
  });
}
function SingleLineLoader({
  size,
  ...rest
}) {
  return jsx(SkeletonLoader, {
    width: "50%",
    height: getFontSizesFromRowSize(size).primary,
    "data-testid": testIds.singleLineLoader,
    ...rest
  });
}
function DoubleLineLoader({
  size
}) {
  const {
    primary,
    secondary
  } = getFontSizesFromRowSize(size);
  return jsxs(React__default.Fragment, {
    children: [jsx(SkeletonLoader, {
      width: "50%",
      mb: 1,
      height: primary,
      "data-testid": testIds.doubleLineLoader
    }), jsx(SkeletonLoader, {
      width: "50%",
      height: secondary
    })]
  });
}
var _ref2 = {
  name: "1d3w5wq",
  styles: "width:100%"
} ;
function NameWithLogoLoader({
  size: size$1
}) {
  const diameter = size$1 === 'md' ? size[24] : size[40];
  return jsxs(Flex, {
    align: "center",
    css: _ref2,
    "data-testid": testIds.nameWithLogoLoader,
    children: [jsx(SkeletonLoader, {
      height: diameter,
      width: diameter,
      radiusSize: "full",
      mr: 1
    }), jsx(SkeletonLoader, {
      width: "50%",
      height: getFontSizesFromRowSize(size$1).primary
    })]
  });
}
const LOADER_MAP = {
  singleLine: SingleLineLoader,
  doubleLine: DoubleLineLoader,
  nameWithLogo: NameWithLogoLoader
};
var _ref3 = {
  name: "1d3w5wq",
  styles: "width:100%"
} ;
function LoadingState({
  justifyContent,
  size,
  variant
}) {
  const hasLoader = typeof variant === 'string';
  if (!hasLoader) return null;
  const Component = LOADER_MAP[variant];
  return jsx(Flex, {
    css: _ref3,
    flexDirection: "column",
    align: ALIGN_MAP[justifyContent] || ALIGN_MAP.start,
    children: jsx(Component, {
      size: size
    })
  });
}
const ROW_SIZE_TO_PRIMARY_TYPESET_SIZE_MAP = {
  md: 'sm',
  lg: 'md',
  auto: 'md'
};
const ROW_SIZE_TO_SECONDARY_TYPESET_SIZE_MAP = {
  md: 'xs',
  lg: 'sm',
  auto: 'sm'
};
function getFontSizesFromRowSize(size) {
  return {
    primary: text.typesets.body[ROW_SIZE_TO_PRIMARY_TYPESET_SIZE_MAP[size]].fontSize,
    secondary: text.typesets.body[ROW_SIZE_TO_SECONDARY_TYPESET_SIZE_MAP[size]].fontSize
  };
}
function getCellStyles(column, userColumnDef) {
  const {
    flex,
    padding
  } = column.columnDef;
  const {
    size: userDefinedSize,
    minSize: minWidth,
    maxSize: maxWidth
  } = userColumnDef;
  const styles = {
    padding,
    minWidth,
    maxWidth,
    flex: isNaN(flex) ? 1 : flex
  };
  if (userDefinedSize) {
    styles.flex = '0 0 auto';
    styles.width = column.getSize();
  }
  return styles;
}
const ExpandableContent = _styled('div', {
  target: "e1rrl8nv1"
})("display:grid;grid-template-rows:", ({
  isExpanded
}) => isExpanded ? '1fr' : '0fr', ";width:100%;border-bottom:", ({
  isExpanded,
  theme
}) => isExpanded ? `1px solid ${theme.colorScheme.list.border.enabled}` : 'none', ";transition:grid-template-rows ", ({
  expandAnimationDuration
}) => expandAnimationDuration, "s ease-in-out,border-bottom 0s ", ({
  isExpanded,
  expandAnimationDuration
}) => isExpanded ? `${expandAnimationDuration}s` : '0s', ";");
const ExpandableInner = _styled('div', {
  target: "e1rrl8nv0"
})({
  name: "1aubja5",
  styles: "overflow:hidden;width:100%"
} );

export { ALIGN_MAP, ButtonCell, DENSITY_TYPESET_MAP, ExpandableContent, ExpandableInner, INTERACTIVE_CELL_DATA_ATTR, INTERACTIVE_CELL_TEXT_UNDERLINE_DATA_ATTR, LinkCell, LoadingState, Resizer, SIZE_MAP, TBody, TCell, TCellBase, THead, THeaderRow, TRow, Table, getCellStyles };
